import {
  ArrowLeft,
  Plus,
  ChevronRight,
  User,
  Building,
  Loader2,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useBeneficiaries } from '@/hooks/useCpn'
import { CpnBeneficiaryUser } from '@/types/cpn'
import { TransferData } from '@/types/payment'

interface BeneficiarySelectionScreenProps {
  transferData: TransferData
  onBeneficiarySelected: (beneficiary: CpnBeneficiaryUser) => void
  onAddBeneficiary: () => void
  onBack: () => void
  userId: string
}

const BeneficiarySelectionScreen = ({
  transferData,
  onBeneficiarySelected,
  onAddBeneficiary,
  onBack,
  userId,
}: BeneficiarySelectionScreenProps) => {
  const [loadingBeneficiaryId, setLoadingBeneficiaryId] = useState<
    string | null
  >(null)

  const {
    data: beneficiariesData,
    isLoading,
    error,
  } = useBeneficiaries(userId, {
    filterCountry: transferData.receiveCountry,
  })

  const beneficiaries = beneficiariesData?.beneficiaryUsers || []

  const handleBeneficiaryClick = (beneficiary: CpnBeneficiaryUser) => {
    setLoadingBeneficiaryId(beneficiary.uuid)
    onBeneficiarySelected(beneficiary)
  }

  return (
    <div className="h-[600px] flex flex-col dark:bg-gray-900 relative">
      {/* Loading Overlay */}

      <div className="p-4 space-y-4 flex-1 overflow-y-auto dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="p-1">
            <ArrowLeft className="w-5 h-5 dark:text-gray-300" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">
              Select Recipient
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Choose or add a Recipient
            </p>
          </div>
        </div>

        {/* Transfer Summary */}
        <Card className="p-3 bg-blue-50 border-blue-200 dark:bg-blue-900 dark:border-blue-700">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-600 dark:text-gray-300">
              Sending to {transferData.receiveCountry}
            </span>
            <span className="font-medium dark:text-white">
              {transferData.sendAmount} {transferData.sendCurrency} →{' '}
              {transferData.receiveAmount} {transferData.receiveCurrency}
            </span>
          </div>
        </Card>

        {/* Beneficiaries List */}
        <div className="space-y-3">
          {isLoading && (
            <Card className="p-4 dark:bg-gray-800 dark:border-gray-700">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                  Loading beneficiaries...
                </span>
              </div>
            </Card>
          )}

          {error && (
            <Card className="p-4 bg-red-50 border-red-200 dark:bg-red-900 dark:border-red-700">
              <p className="text-sm text-red-600 dark:text-red-400">
                Failed to load beneficiaries. Please try again.
              </p>
            </Card>
          )}

          {!isLoading && !error && beneficiaries.length === 0 && (
            <Card className="p-4 text-center dark:bg-gray-800 dark:border-gray-700">
              <User className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No saved beneficiaries
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Add a beneficiary to get started
              </p>
            </Card>
          )}

          {!isLoading &&
            beneficiaries.map((beneficiary) => {
              const isMatchingCountry =
                beneficiary.country === transferData.receiveCountry
              const isLoadingThis = loadingBeneficiaryId === beneficiary.uuid

              return (
                <Card
                  key={beneficiary.uuid}
                  className={`p-4 transition-all duration-200 border-gray-200 dark:border-gray-700 ${
                    isMatchingCountry
                      ? 'cursor-pointer hover:shadow-md dark:bg-gray-800 dark:hover:bg-gray-700 hover:bg-gray-50'
                      : 'opacity-40 cursor-not-allowed bg-gray-50 dark:bg-gray-800/50'
                  }`}
                  onClick={
                    isMatchingCountry && !isLoadingThis
                      ? () => handleBeneficiaryClick(beneficiary)
                      : undefined
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          isMatchingCountry
                            ? 'bg-gray-100 dark:bg-gray-700'
                            : 'bg-gray-200 dark:bg-gray-600'
                        }`}
                      >
                        {beneficiary.isBusiness ? (
                          <Building
                            className={`w-5 h-5 ${
                              isMatchingCountry
                                ? 'text-gray-600 dark:text-gray-400'
                                : 'text-gray-500 dark:text-gray-500'
                            }`}
                          />
                        ) : (
                          <span
                            className={`text-sm font-medium ${
                              isMatchingCountry
                                ? 'text-gray-600 dark:text-gray-400'
                                : 'text-gray-500 dark:text-gray-500'
                            }`}
                          >
                            {(beneficiary.firstName?.[0] || '').toUpperCase()}
                            {(beneficiary.lastName?.[0] || '').toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <h3
                          className={`font-medium ${
                            isMatchingCountry
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {beneficiary.isBusiness
                            ? beneficiary.firstName
                            : `${beneficiary.firstName} ${beneficiary.lastName}`}
                        </h3>
                        {beneficiary.isBusiness && (
                          <p
                            className={`text-xs ${
                              isMatchingCountry
                                ? 'text-gray-500 dark:text-gray-400'
                                : 'text-gray-400 dark:text-gray-500'
                            }`}
                          >
                            Contact: {beneficiary.lastName}
                          </p>
                        )}
                        <p
                          className={`text-sm ${
                            isMatchingCountry
                              ? 'text-gray-600 dark:text-gray-400'
                              : 'text-gray-500 dark:text-gray-500'
                          }`}
                        >
                          {beneficiary.city && beneficiary.country
                            ? `${beneficiary.city}, ${beneficiary.country}`
                            : beneficiary.country || 'No address'}
                        </p>
                      </div>
                    </div>
                    {isLoadingThis ? (
                      <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                    ) : (
                      <ChevronRight
                        className={`w-5 h-5 ${
                          isMatchingCountry
                            ? 'text-gray-400 dark:text-gray-500'
                            : 'text-gray-400 dark:text-gray-600'
                        }`}
                      />
                    )}
                  </div>
                </Card>
              )
            })}
        </div>
      </div>

      {/* Add Beneficiary Button */}
      <div className="p-4 border-t border-gray-100 dark:border-gray-700 dark:bg-gray-900">
        <Button
          onClick={onAddBeneficiary}
          variant="outline"
          className="w-full flex items-center justify-center space-x-2 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 transition-colors h-12"
        >
          <Plus className="w-4 h-4 dark:text-gray-300" />
          <span>Add Recipient</span>
        </Button>
      </div>
    </div>
  )
}

export default BeneficiarySelectionScreen
